import { useState, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import type { Entity } from '@/modules/shared/types/entity.type'
import type { PayloadSearch } from '@/modules/shared/types/payload-search.type'
import { type EntitySearchPayload, entitySearchService } from '@/modules/shared/services/entity-search.service'

export function useClientInfiniteSearch() {
  const [clients, setClients] = useState<Entity[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [currentSearchPayload, setCurrentSearchPayload] = useState<EntitySearchPayload | null>(null)

  const isSearchingRef = useRef(false)

  const resetSearch = useCallback(() => {
    setClients([])
    setIsLoading(false)
    setIsLoadingMore(false)
    setHasMore(false)
    setTotalRecords(0)
    setCurrentPage(1)
    setCurrentSearchPayload(null)
    isSearchingRef.current = false
  }, [])

  const searchClients = useCallback(async ({ payload }: PayloadSearch) => {
    if (payload.inputValue.length === 0) {
      payload.inputValue = '%20'
    }

    if (isSearchingRef.current) {
      return
    }

    isSearchingRef.current = true
    setIsLoading(true)
    setClients([])
    setCurrentPage(1)

    const searchPayload: EntitySearchPayload = {
      entityType: '1',
      filter: payload.filter,
      inputValue: payload.inputValue,
      dataInclusao: undefined
    }

    try {

      const response = await entitySearchService.searchInitialEntities(searchPayload)
      setClients(response.entities)
      setTotalRecords(response.totalRecords)
      setHasMore(response.hasMore)
      setCurrentSearchPayload(searchPayload)
      setCurrentPage(2)

    } catch (error) {
      toast.error('Erro ao buscar clientes')
      setClients([])
      setTotalRecords(0)
      setHasMore(false)
    } finally {
      setIsLoading(false)
      isSearchingRef.current = false
    }
  }, [])

  const loadMore = useCallback(async () => {
    if (!currentSearchPayload || !hasMore || isLoadingMore || isSearchingRef.current) {

      return
    }

    setIsLoadingMore(true)

    try {


      const response = await entitySearchService.loadMoreEntities(
        currentSearchPayload,
        currentPage,
        clients.length,
        totalRecords
      )

      if (response.entities.length > 0) {
        setClients(prev => [...prev, ...response.entities])
        setCurrentPage(prev => prev + 1)
      }

      setHasMore(response.hasMore)

    } catch (error) {

      toast.error('Erro ao carregar mais clientes')
    } finally {
      setIsLoadingMore(false)
    }
  }, [currentSearchPayload, hasMore, isLoadingMore, currentPage, clients.length, totalRecords])

  return {
    clients,
    isLoading,
    isLoadingMore,
    hasMore,
    totalRecords,
    searchClients,
    loadMore,
    resetSearch
  }
}